import { MessageDialog, useDialogManager } from '../../../../../../widgets/dialog';
import { BasicButton } from '../../../../../../widgets/buttons';
import { FormControl } from '../../../../../../widgets/formControl';
import { Input } from '../../../../../../widgets/input';
import { Radio } from '../../../../../../widgets/radio';
import { ZTooltip } from '../../../../../../widgets/ztooltip';
import { Tab, TabList, TabPanel } from '../../../../../../widgets/ztabs';
import React, { FC, useCallback } from 'react';
import { AdvancedSelect } from '../../../../../../widgets/select';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { ascComparator, T } from 'src/shared/helpers';
import GroupIcon from 'src/shared/material_icons/action/join_inner.svg';
import StepIcon from 'src/shared/material_icons/maps/layers.svg';
import styled from 'styled-components';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import ComposerRetargetAttributeDialog from '../../../configurator-composer/composer/dialogs/composerRetargetAttributeDialog';
import ComposerSaveActionDialog from '../../../configurator-composer/composer/dialogs/save/composerSaveActionDialog';
import ComposerUploadComponent from '../../../configurator-composer/composer/components/composerUploadComponent';
import {
    getComposerSelectionDescendants,
    getComposerStructureSelectionRules,
    getComposerStructureSelectionSummary
} from '../../../configurator-composer/composer/helpers/composerBulkSelection';
import { applyComposerSharedActionEdit, getComposerSharedOptionActions } from '../../../configurator-composer/composer/helpers/composerBulkOptionActions';
import { getRetargetObjectsAndMaterials, retargetMapToActions } from '../../../configurator-composer/composer/helpers/composerHelpers';
import { useTreeNodeManager } from '../../tree';
import { PropertiesPanelDock, WORKSPACE_PANEL_CONTENT_MAX, WORKSPACE_PANEL_CONTENT_MIN } from '../../../configurator-scene-editor/useWorkspacePreferences';
import {
    ComposerDetailCard,
    ComposerDetailLabel,
    ComposerDetailValue,
    ComposerEmptyState,
    ComposerFormGroup,
    ComposerFormSection,
    ComposerFormSectionTitle,
    ComposerFormStack,
    ComposerKindBadge,
    ComposerPanel,
    ComposerRadioRow,
    ComposerResizeHandle,
    ComposerScrollBody,
    ComposerSettingsBody,
    ComposerSubjectContent,
    ComposerSubjectHeader,
    ComposerSubjectMark,
    ComposerSubjectMeta,
    ComposerSubjectName,
    ComposerSubjectNameInput,
    ComposerTabs,
    ComposerUnsupportedNote
} from '../composerWorkspace';
import ComposerPreview from '../composerPreview';
import ComposerActions from './composerActions';
import { getComposerActionDefinition } from './composerActionCatalog';
import {
    ComposerAreaNode,
    ComposerAttributeNode,
    ComposerAttributeOptionNode,
    ComposerAttributeType,
    ComposerOptionActionNode
} from './composerAttributesModels';
import ComposerOptions from './composerOptions';
import ComposerPresetConfigurations from '../../../configurator-composer/composer/components/composerPresetConfigurations';
import CustomizableAreasEditor from '../customizableAreasEditor';
import { ComposerValidationErrorCode, hasComposerValidationError } from '../../../configurator-composer/composer/helpers/composerAttributeValidation';

const EMPTY_OPTIONS: ComposerAttributeOptionNode[] = [];
const EMPTY_ACTIONS: ComposerOptionActionNode[] = [];

const WorkspaceGrid = styled.div<{ $propertiesPanel: PropertiesPanelDock; $detailsWidth?: number; $hasOptionsPanel: boolean }>`
    display: grid;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    grid-template-columns: ${(props) =>
        props.$hasOptionsPanel
            ? props.$propertiesPanel === 'side'
                ? `minmax(230px, 2fr) 6px ${props.$detailsWidth ? `${props.$detailsWidth}px` : 'minmax(320px, 3fr)'} 6px minmax(0, 5fr)`
                : `minmax(0, 5fr) 6px minmax(230px, 2fr) 6px ${props.$detailsWidth ? `${props.$detailsWidth}px` : 'minmax(320px, 3fr)'}`
            : props.$propertiesPanel === 'side'
              ? `${props.$detailsWidth ? `${props.$detailsWidth}px` : 'minmax(350px, var(--c2, 3fr))'} 6px minmax(0, var(--c3, 5fr))`
              : `minmax(0, var(--c3, 5fr)) 6px ${props.$detailsWidth ? `${props.$detailsWidth}px` : 'minmax(350px, var(--c2, 3fr))'}`};
    overflow: hidden;
    background: var(--composer-app, #f0f0f0);

    @media (max-width: 940px) {
        grid-template-columns: 1fr;

        > section:not(:last-child),
        > [role='separator'] {
            display: none;
        }

        > section:last-child,
        > :last-child {
            grid-area: 1 / 1;
        }
    }
`;

const OptionsBoundary = styled.div`
    width: 6px;

    @media (max-width: 940px) {
        display: none;
    }
`;

const SelectionList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const SelectionItem = styled.div`
    padding: 9px 10px;
    color: ${(props) => props.theme.app.textColor};
    background: ${(props) => props.theme.app.backgroundColorBase};
    border: 1px solid ${(props) => props.theme.app.borderColor};
    border-radius: ${(props) => props.theme.app.component.card.borderRadius};
    font-size: 13px;
`;

const BulkButtons = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 12px;

    > button,
    > div,
    > div > button {
        width: 100%;
    }
`;

const SharedActions = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    margin-top: 14px;
`;

const SharedActionCard = styled(ComposerDetailCard)`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 10px;

    ${ComposerDetailValue} {
        color: var(--composer-muted, #5c6b7a);
        font-size: 12px;
    }
`;

interface BulkActionButtonProps extends React.ComponentProps<typeof BasicButton> {
    disabledReason?: string;
}

const BulkActionButton: FC<BulkActionButtonProps> = ({ children, disabled, disabledReason, ...buttonProps }) => {
    const button = (
        <BasicButton {...buttonProps} disabled={disabled}>
            {children}
        </BasicButton>
    );

    return disabled && disabledReason ? (
        <ZTooltip addContainer compact position='left' title={disabledReason}>
            {button}
        </ZTooltip>
    ) : (
        button
    );
};

const SubjectPanel: FC<{ subject?: ComposerAttributeNode; attributes: ComposerAttributeNode[] }> = ({ subject, attributes }) => {
    const [updateAttribute, validateAttributes, sceneObjects] = useComposerStore((store) => [
        store.updateAttribute,
        store.validateAttributes,
        store.sceneObjectList
    ]);
    const [zkScene, sceneEditor] = usePreview3DStore((store) => [store.zkScene, store.sceneEditor]);

    if (!subject) {
        return (
            <ComposerPanel>
                <ComposerEmptyState>{T._('Select one group, step or attribute to inspect it.', 'Admin')}</ComposerEmptyState>
            </ComposerPanel>
        );
    }

    const isGroup = subject.type === ComposerAttributeType.AttributeGroup;
    const typeLabel = isGroup ? T._('Group', 'Admin') : T._('Step', 'Admin');
    const childAttributes = attributes.filter((attribute) => attribute.parentID === subject.id);
    const cameraOptions =
        zkScene
            ?.get?.('cameraLocations')
            ?.models?.map((location) => ({ value: location.getLocationID(), label: location.get('name') || location.getLocationID() })) ?? [];
    const objectOptions = sceneObjects.filter((mesh) => sceneEditor?.isRealMesh(mesh)).map((mesh) => ({ value: mesh.id, label: mesh.name || mesh.id }));
    const selectedObjects = (subject.objects || '')
        .split(/[;,]/)
        .filter(Boolean)
        .map((id) => ({ value: id, label: objectOptions.find((option) => option.value === id)?.label || id }));
    const updateSubject = (changes: Partial<ComposerAttributeNode>) => updateAttribute({ ...subject, ...changes, isNew: true });
    const updateGroupImage = async (imageUrl: string, tempImageFileKey?: string) => {
        updateSubject({
            img: imageUrl,
            data: {
                ...subject.data,
                imageUrl,
                tempImageUrl: imageUrl,
                tempImageFileKey: tempImageFileKey ?? ''
            }
        });
        return imageUrl;
    };

    return (
        <ComposerPanel>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind={isGroup ? 'group' : 'neutral'}>
                    {subject.img ? <img src={subject.img} alt='' /> : isGroup ? <GroupIcon /> : <StepIcon />}
                </ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectNameInput
                        aria-label={isGroup ? T._('Group name', 'Admin') : T._('Step name', 'Admin')}
                        placeholder={isGroup ? T._('Enter group name', 'Admin') : T._('Enter step name', 'Admin')}
                        isError={hasComposerValidationError(
                            subject,
                            isGroup ? ComposerValidationErrorCode.GROUP_NAME_REQUIRED : ComposerValidationErrorCode.STEP_NAME_REQUIRED
                        )}
                        value={subject.name}
                        onChange={(event) => updateSubject({ name: event.target.value })}
                        onBlur={validateAttributes}
                    />
                    <ComposerSubjectMeta>
                        <ComposerKindBadge $kind={isGroup ? 'group' : 'neutral'}>{typeLabel}</ComposerKindBadge>
                        <span>{T._(`${childAttributes.length} attributes`, 'Admin')}</span>
                    </ComposerSubjectMeta>
                </ComposerSubjectContent>
            </ComposerSubjectHeader>
            {/* Neither subject carries a constraint expression the product input can store, so the panel offers Settings alone.
                The shared face is left untouched, so the Links face an attribute was on survives a detour through a group. */}
            <ComposerTabs selectedIndex={0}>
                <TabList>
                    <Tab>{T._('Settings', 'Admin')}</Tab>
                </TabList>
                <TabPanel>
                    <ComposerSettingsBody>
                        <ComposerFormStack>
                            {isGroup && (
                                <ComposerFormGroup>
                                    <ComposerFormSectionTitle>{T._('Appearance', 'Admin')}</ComposerFormSectionTitle>
                                    <ComposerFormSection>
                                        <FormControl
                                            label={T._('Group image', 'Admin')}
                                            tooltipLabelHtml={T._('The image displayed for this group in the configurator.', 'Admin')}
                                        >
                                            <ComposerUploadComponent passedImgUrl={subject.img} onImagePick={updateGroupImage} />
                                        </FormControl>
                                        <FormControl
                                            label={T._('Attributes direction', 'Admin')}
                                            tooltipLabelHtml={T._(
                                                'Choose how attributes are displayed in the configurator: in a row or column. This does not affect mobile devices.',
                                                'Admin'
                                            )}
                                        >
                                            <ComposerRadioRow>
                                                <Radio
                                                    id={`composer-group-${subject.id}-horizontal`}
                                                    label={T._('Horizontal', 'Admin')}
                                                    name={`composer-group-${subject.id}-direction`}
                                                    value={MPlaza.AttributeGroupDirection.Horizontal as number}
                                                    checked={subject.direction !== MPlaza.AttributeGroupDirection.Vertical}
                                                    onSelect={(direction) => updateSubject({ direction })}
                                                />
                                                <Radio
                                                    id={`composer-group-${subject.id}-vertical`}
                                                    label={T._('Vertical', 'Admin')}
                                                    name={`composer-group-${subject.id}-direction`}
                                                    value={MPlaza.AttributeGroupDirection.Vertical as number}
                                                    checked={subject.direction === MPlaza.AttributeGroupDirection.Vertical}
                                                    onSelect={(direction) => updateSubject({ direction })}
                                                />
                                            </ComposerRadioRow>
                                        </FormControl>
                                        {subject.direction === MPlaza.AttributeGroupDirection.Vertical && (
                                            <FormControl
                                                label={T._('Attributes state', 'Admin')}
                                                tooltipLabelHtml={T._(
                                                    'Choose how attributes are opened in the configurator. This does not affect mobile devices.',
                                                    'Admin'
                                                )}
                                            >
                                                <ComposerRadioRow>
                                                    <Radio
                                                        id={`composer-group-${subject.id}-accordion`}
                                                        label={T._('First open, others with accordion', 'Admin')}
                                                        name={`composer-group-${subject.id}-attributes-state`}
                                                        value='accordion'
                                                        checked={!subject.attributesAlwaysOpened}
                                                        onSelect={() => updateSubject({ attributesAlwaysOpened: false })}
                                                    />
                                                    <Radio
                                                        id={`composer-group-${subject.id}-all-open`}
                                                        label={T._('All attributes opened', 'Admin')}
                                                        name={`composer-group-${subject.id}-attributes-state`}
                                                        value='all-open'
                                                        checked={!!subject.attributesAlwaysOpened}
                                                        onSelect={() => updateSubject({ attributesAlwaysOpened: true })}
                                                    />
                                                </ComposerRadioRow>
                                            </FormControl>
                                        )}
                                    </ComposerFormSection>
                                </ComposerFormGroup>
                            )}
                            <ComposerFormGroup>
                                <ComposerFormSectionTitle>{T._('Camera', 'Admin')}</ComposerFormSectionTitle>
                                <ComposerFormSection>
                                    <FormControl
                                        label={T._('Camera position', 'Admin')}
                                        tooltipLabelHtml={
                                            isGroup
                                                ? T._('The camera moves to this position when the customer selects this group in the configurator.', 'Admin')
                                                : T._('The camera moves to this position when the customer opens this step in the configurator.', 'Admin')
                                        }
                                    >
                                        <AdvancedSelect
                                            isClearable
                                            options={cameraOptions}
                                            value={cameraOptions.find((option) => option.value === subject.camera) ?? null}
                                            onChange={(option) => updateSubject({ camera: option?.value || '' })}
                                            placeholder={T._('Select a camera position', 'Admin')}
                                            menuPosition='fixed'
                                        />
                                    </FormControl>
                                    {isGroup && (
                                        <FormControl
                                            label={T._('Objects', 'Admin')}
                                            tooltipLabelHtml={T._('When you click on these objects the camera will move to the selected position', 'Admin')}
                                        >
                                            <AdvancedSelect
                                                isMulti
                                                options={objectOptions}
                                                value={selectedObjects}
                                                onChange={(options) =>
                                                    updateSubject({ objects: options.map((option: { value: string }) => option.value).join(';') })
                                                }
                                                placeholder={T._('Select objects…', 'Admin')}
                                                menuPosition='fixed'
                                            />
                                        </FormControl>
                                    )}
                                </ComposerFormSection>
                            </ComposerFormGroup>
                            <ComposerFormGroup>
                                <ComposerFormSectionTitle>{T._('Attributes', 'Admin')}</ComposerFormSectionTitle>
                                <ComposerFormSection>
                                    <ComposerDetailValue>
                                        {childAttributes.map((attribute) => attribute.name).join(', ') || T._('No attributes assigned', 'Admin')}
                                    </ComposerDetailValue>
                                </ComposerFormSection>
                            </ComposerFormGroup>
                        </ComposerFormStack>
                    </ComposerSettingsBody>
                </TabPanel>
            </ComposerTabs>
        </ComposerPanel>
    );
};

const MultipleAttributesPanel: FC<{ attributes: ComposerAttributeNode[] }> = ({ attributes }) => {
    const { showDialog, closeDialog } = useDialogManager();
    const [allAttributes, cloneAttributes, removeAttributes, updateAttribute, selectAttributes, selectOptions, setAttributeFace, materials, objects] =
        useComposerStore((store) => [
            store.attributes,
            store.cloneAttributes,
            store.removeAttributes,
            store.updateAttribute,
            store.selectAttributes,
            store.selectOptions,
            store.setAttributeFace,
            store.sceneMaterialList,
            store.sceneObjectList
        ]);
    const selectionRules = getComposerStructureSelectionRules(attributes);
    const descendants = getComposerSelectionDescendants(attributes, allAttributes);
    const affectedRows = [...descendants, ...attributes];
    const removalSummary = getComposerStructureSelectionSummary(affectedRows);
    const actionableAttributes = [...attributes, ...descendants].filter((attribute) => attribute.type === ComposerAttributeType.Attribute);
    const actions = actionableAttributes.flatMap((attribute) => attribute.options.flatMap((option) => option.actions));

    const duplicateSelection = () => {
        if (!selectionRules.canDuplicate) return;
        const cloned = cloneAttributes(affectedRows);
        const clonedSelection = cloned.slice(Math.max(0, cloned.length - attributes.length));
        selectAttributes(clonedSelection);
        selectOptions(null);
    };

    const setSelectionEnabled = (enabled: boolean) => {
        attributes
            .filter((attribute) => attribute.type !== ComposerAttributeType.PreSetContainer)
            .forEach((attribute) => updateAttribute({ ...attribute, enabled }));
    };

    const manageSelectedAttributeLinks = () => {
        if (!selectionRules.canManageLinks) return;
        selectAttributes([attributes[0]]);
        selectOptions(null);
        setAttributeFace('links');
    };

    const removeSelection = () => {
        showDialog(
            'confirmDialog',
            <MessageDialog
                title={T._('Confirm Removal', 'Admin')}
                message={T._(
                    `Do you want remove ${removalSummary.attributes} attributes, ${removalSummary.groups} groups, ${removalSummary.presetConfigurations} pre-set configurations and ${removalSummary.steps} steps?`,
                    'Admin'
                )}
                buttons={[
                    { label: T._('Cancel', 'Admin'), outline: true, onClick: () => closeDialog('confirmDialog') },
                    {
                        label: T._('Remove', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            closeDialog('confirmDialog');
                            removeAttributes(affectedRows.map((attribute) => attribute.id));
                            selectAttributes(null);
                            selectOptions(null);
                        }
                    }
                ]}
            />
        );
    };

    const retargetSelection = () => {
        if (!selectionRules.canRetarget) return;
        const { objectIds, materialIds } = getRetargetObjectsAndMaterials(actions);
        showDialog(
            'composerRetargetAttribute',
            <ComposerRetargetAttributeDialog
                materialsList={materials}
                objectsList={objects}
                usedMaterialsList={materials.filter((material) => materialIds.includes(material.id))}
                usedObjectsList={objects.filter((object) => objectIds.includes(object.id) || objectIds.includes(object.name || ''))}
                onConfirm={(data) => {
                    closeDialog('composerRetargetAttribute');
                    if (!data) return;
                    const remapped = retargetMapToActions(actions, data.materialMap, data.objectMap);
                    actionableAttributes.forEach((attribute) =>
                        updateAttribute({
                            ...attribute,
                            options: attribute.options.map((option) => ({
                                ...option,
                                actions: option.actions.map((action) => remapped.find((candidate) => candidate.id === action.id) ?? action)
                            }))
                        })
                    );
                }}
            />
        );
    };

    return (
        <ComposerPanel>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind='neutral'>{attributes.length}</ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectName>{T._('Structure selection', 'Admin')}</ComposerSubjectName>
                    <ComposerSubjectMeta>{T._(`${attributes.length} selected`, 'Admin')}</ComposerSubjectMeta>
                </ComposerSubjectContent>
            </ComposerSubjectHeader>
            <ComposerScrollBody>
                <SelectionList>
                    {attributes.map((attribute) => (
                        <SelectionItem key={attribute.id}>{attribute.name}</SelectionItem>
                    ))}
                </SelectionList>
                <BulkButtons>
                    <BulkActionButton
                        disabled={!selectionRules.canEnable}
                        disabledReason={T._('All selected rows are already enabled.', 'Admin')}
                        onClick={() => setSelectionEnabled(true)}
                    >
                        {T._(`Enable ${attributes.length} rows`, 'Admin')}
                    </BulkActionButton>
                    <BulkActionButton
                        disabled={!selectionRules.canDisable}
                        disabledReason={T._('All selected rows are already disabled.', 'Admin')}
                        onClick={() => setSelectionEnabled(false)}
                    >
                        {T._(`Disable ${attributes.length} rows`, 'Admin')}
                    </BulkActionButton>
                    <BulkActionButton
                        disabled={!selectionRules.canManageLinks}
                        disabledReason={T._('Select only attributes.', 'Admin')}
                        onClick={manageSelectedAttributeLinks}
                    >
                        {T._('Manage links…', 'Admin')}
                    </BulkActionButton>
                    <BulkActionButton
                        disabled={!selectionRules.canRetarget}
                        disabledReason={T._('Select only groups and attributes.', 'Admin')}
                        onClick={retargetSelection}
                    >
                        {T._('Retarget selection…', 'Admin')}
                    </BulkActionButton>
                    <BulkActionButton
                        disabled={!selectionRules.canDuplicate}
                        disabledReason={T._('Select only groups, steps or attributes but not mixed.', 'Admin')}
                        onClick={duplicateSelection}
                    >
                        {T._(`Duplicate ${attributes.length} rows`, 'Admin')}
                    </BulkActionButton>
                    <BasicButton onClick={removeSelection}>{T._(`Remove ${attributes.length} rows`, 'Admin')}</BasicButton>
                </BulkButtons>
            </ComposerScrollBody>
        </ComposerPanel>
    );
};

const MultipleOptionsPanel: FC<{
    selectedAttribute: ComposerAttributeNode;
    options: ComposerAttributeOptionNode[];
    materials: BABYLON.Material[];
    meshes: BABYLON.AbstractMesh[];
    cameras: BABYLON.Camera[];
}> = ({ selectedAttribute, options, materials, meshes, cameras }) => {
    const { showDialog, closeDialog } = useDialogManager();
    const [cloneOption, removeOption, selectOptions, updateOption, updateAction] = useComposerStore((store) => [
        store.cloneOption,
        store.removeOption,
        store.selectOptions,
        store.updateOption,
        store.updateAction
    ]);
    const sharedActions = React.useMemo(() => getComposerSharedOptionActions(options), [options]);

    const cloneSelection = () => {
        const cloned = options.map(cloneOption);
        selectOptions(cloned);
    };

    const setSelectionEnabled = (enabled: boolean) => {
        options.forEach((option) => updateOption({ ...option, enabled }));
    };

    const removeSelection = () => {
        showDialog(
            'confirmDialog',
            <MessageDialog
                title={T._('Confirm Removal', 'Admin')}
                message={T._('Are you sure you want to remove the selected options?', 'Admin')}
                buttons={[
                    { label: T._('Cancel', 'Admin'), outline: true, onClick: () => closeDialog('confirmDialog') },
                    {
                        label: T._('Remove', 'Admin'),
                        destructive: true,
                        onClick: () => {
                            closeDialog('confirmDialog');
                            options.forEach((option) => removeOption(option.attributeID, option.id));
                            selectOptions([]);
                        }
                    }
                ]}
            />
        );
    };

    const editSharedAction = (sharedAction: (typeof sharedActions)[number]) => {
        const definition = getComposerActionDefinition(sharedAction.representative.type);
        showDialog(
            'composerBulkEditAction',
            <ComposerSaveActionDialog
                edit
                title={T._(`Edit ${definition.name} for ${options.length} options`, 'Admin')}
                confirmLabel={T._(`Apply to ${options.length} options`, 'Admin')}
                passedAction={sharedAction.representative}
                attributeID={selectedAttribute.id}
                optionID={sharedAction.representative.optionID}
                materials={materials}
                meshes={meshes}
                cameras={cameras}
                onConfirm={(editedAction) => {
                    if (!editedAction) return;
                    applyComposerSharedActionEdit(sharedAction.actions, editedAction).forEach(updateAction);
                    closeDialog('composerBulkEditAction');
                }}
            />
        );
    };

    return (
        <ComposerPanel>
            <ComposerSubjectHeader>
                <ComposerSubjectMark $kind='option'>{options.length}</ComposerSubjectMark>
                <ComposerSubjectContent>
                    <ComposerSubjectName>{T._('Options', 'Admin')}</ComposerSubjectName>
                    <ComposerSubjectMeta>{T._(`${options.length} selected`, 'Admin')}</ComposerSubjectMeta>
                </ComposerSubjectContent>
            </ComposerSubjectHeader>
            <ComposerScrollBody>
                <SelectionList>
                    {options.map((option) => (
                        <SelectionItem key={option.id}>{option.name}</SelectionItem>
                    ))}
                </SelectionList>
                <SharedActions>
                    <ComposerFormSectionTitle>{T._('Shared actions', 'Admin')}</ComposerFormSectionTitle>
                    {sharedActions.length === 0 ? (
                        <ComposerUnsupportedNote>
                            {T._(
                                'The selected options do not have an action that can be matched safely. An action must occur exactly once in every selected option.',
                                'Admin'
                            )}
                        </ComposerUnsupportedNote>
                    ) : (
                        <>
                            {sharedActions.map((sharedAction) => {
                                const definition = getComposerActionDefinition(sharedAction.representative.type);
                                const target = sharedAction.representative.selector || sharedAction.representative.property || T._('No target', 'Admin');
                                const value = sharedAction.hasMixedValues
                                    ? T._('Mixed values', 'Admin')
                                    : sharedAction.representative.value || T._('No value', 'Admin');
                                return (
                                    <SharedActionCard key={sharedAction.key}>
                                        <div>
                                            <ComposerDetailLabel>{definition.name}</ComposerDetailLabel>
                                            <ComposerDetailValue>{`${target} · ${value}`}</ComposerDetailValue>
                                        </div>
                                        <BasicButton $size='small' onClick={() => editSharedAction(sharedAction)}>
                                            {T._(`Edit for ${options.length} options…`, 'Admin')}
                                        </BasicButton>
                                    </SharedActionCard>
                                );
                            })}
                            <ComposerUnsupportedNote>
                                {T._('Only actions present exactly once in every selected option can be edited together.', 'Admin')}
                            </ComposerUnsupportedNote>
                        </>
                    )}
                </SharedActions>
                <BulkButtons>
                    <BulkActionButton
                        disabled={options.every((option) => option.enabled !== false)}
                        disabledReason={T._('All selected options are already enabled.', 'Admin')}
                        onClick={() => setSelectionEnabled(true)}
                    >
                        {T._('Enable all options', 'Admin')}
                    </BulkActionButton>
                    <BulkActionButton
                        disabled={options.every((option) => option.enabled === false)}
                        disabledReason={T._('All selected options are already disabled.', 'Admin')}
                        onClick={() => setSelectionEnabled(false)}
                    >
                        {T._('Disable all options', 'Admin')}
                    </BulkActionButton>
                    <BasicButton onClick={cloneSelection}>{T._(`Clone ${options.length} options`, 'Admin')}</BasicButton>
                    <BasicButton onClick={removeSelection}>{T._(`Remove ${options.length} options`, 'Admin')}</BasicButton>
                </BulkButtons>
            </ComposerScrollBody>
        </ComposerPanel>
    );
};

const ResizeBoundary: FC<{
    label: string;
    onResize: (width: number) => void;
    onReset: () => void;
}> = ({ label, onResize, onReset }) => {
    // The same floor and ceiling the Scene Editor columns are held to.
    const clampWidth = (width: number) => Math.min(Math.max(width, WORKSPACE_PANEL_CONTENT_MIN), WORKSPACE_PANEL_CONTENT_MAX);
    const startResize = (event: React.MouseEvent<HTMLDivElement>) => {
        const previousPanel = event.currentTarget.previousElementSibling as HTMLElement | null;
        if (!previousPanel) return;
        const startX = event.clientX;
        const startWidth = previousPanel.getBoundingClientRect().width;
        const move = (moveEvent: MouseEvent) => onResize(clampWidth(startWidth + moveEvent.clientX - startX));
        const stop = () => {
            window.removeEventListener('mousemove', move);
            window.removeEventListener('mouseup', stop);
        };
        window.addEventListener('mousemove', move);
        window.addEventListener('mouseup', stop);
    };

    return (
        <ComposerResizeHandle
            aria-label={label}
            aria-orientation='vertical'
            onDoubleClick={onReset}
            onKeyDown={(event) => {
                const previousPanel = event.currentTarget.previousElementSibling as HTMLElement | null;
                if (!previousPanel || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;
                event.preventDefault();
                onResize(clampWidth(previousPanel.getBoundingClientRect().width + (event.key === 'ArrowRight' ? 16 : -16)));
            }}
            onMouseDown={startResize}
            role='separator'
            tabIndex={0}
            title={T._('Drag to resize · double-click to reset', 'Admin')}
        />
    );
};

const ComposerAttributeContentPage: FC<{
    propertiesPanel: PropertiesPanelDock;
    onOpenFullEditor?: (area: ComposerAreaNode, templateNodeID: string) => Promise<boolean | void>;
    onPreparePresetEditor: (emptyPresetID?: string) => Promise<boolean>;
}> = ({ propertiesPanel, onOpenFullEditor, onPreparePresetEditor }) => {
    const [attributes, selectedAttributes, selectedOptions, storeMeshList, storeMaterialsList, storeCameraList] = useComposerStore((store) => [
        store.attributes,
        store.selectedAttributes,
        store.selectedOptions,
        store.sceneObjectList,
        store.sceneMaterialList,
        store.sceneCameraList
    ]);
    const sceneEditor = usePreview3DStore((store) => store.sceneEditor);
    const [detailsWidth, setDetailsWidth] = React.useState<number>();
    const [areaDetailsHost, setAreaDetailsHost] = React.useState<HTMLElement | null>(null);
    const selectedAttribute = selectedAttributes?.[0] ?? null;
    const isAreaAttribute =
        selectedAttributes?.length === 1 &&
        selectedAttribute?.type === ComposerAttributeType.Attribute &&
        selectedAttribute.attributeType === MPlaza.AttributeType.CustomizableArea;
    const hasOptionsPanel =
        selectedAttributes?.length === 1 &&
        selectedAttribute?.type === ComposerAttributeType.Attribute &&
        selectedAttribute.attributeType !== MPlaza.AttributeType.CustomizableArea;
    const hasDetailsPanel = hasOptionsPanel || isAreaAttribute;
    const options = selectedAttribute?.options ?? EMPTY_OPTIONS;
    const actions = selectedOptions?.length === 1 ? selectedOptions[0].actions : EMPTY_ACTIONS;
    const multipleAttributes = (selectedAttributes?.length ?? 0) > 1;
    const multipleOptions = (selectedOptions?.length ?? 0) > 1;
    const createOptionOverlay = useCallback(() => () => null, []);
    const createActionOverlay = useCallback(() => () => null, []);
    const optionTreeState = useTreeNodeManager<ComposerAttributeOptionNode>(options, createOptionOverlay);
    const actionsTreeState = useTreeNodeManager<ComposerOptionActionNode>(actions, createActionOverlay);
    const meshes = storeMeshList.filter((mesh) => sceneEditor?.isRealMesh(mesh)).sort(ascComparator((mesh) => mesh.name ?? ''));
    const materials = storeMaterialsList
        .filter((material) => material.id !== 'default material' && material.name !== 'default material')
        .sort(ascComparator((material) => material.name ?? ''));
    const cameras = storeCameraList
        .filter((camera) => camera.id !== 'default camera' && camera.name !== 'default camera')
        .sort(ascComparator((camera) => camera.name ?? ''));

    const inspectorPanel = multipleAttributes ? (
        <MultipleAttributesPanel attributes={selectedAttributes ?? []} />
    ) : selectedAttribute?.type === ComposerAttributeType.PreSetContainer ? (
        <ComposerPresetConfigurations key={selectedAttribute.id} attribute={selectedAttribute} onPrepareEditor={onPreparePresetEditor} />
    ) : selectedAttribute?.type === ComposerAttributeType.Attribute && !multipleAttributes ? (
        selectedAttribute.attributeType === MPlaza.AttributeType.CustomizableArea ? (
            <CustomizableAreasEditor selectedAttribute={selectedAttribute} onOpenFullEditor={onOpenFullEditor} detailsHost={areaDetailsHost} />
        ) : (
            <ComposerOptions selectedAttribute={selectedAttribute} options={options} multiple={false} state={optionTreeState} />
        )
    ) : (
        <SubjectPanel subject={selectedAttribute ?? undefined} attributes={attributes} />
    );

    const optionDetailsPanel =
        multipleOptions && selectedAttribute ? (
            <MultipleOptionsPanel
                selectedAttribute={selectedAttribute}
                options={selectedOptions ?? []}
                materials={materials}
                meshes={meshes}
                cameras={cameras}
            />
        ) : selectedOptions?.length === 1 && selectedAttribute ? (
            <ComposerActions
                selectedAttribute={selectedAttribute}
                selectedOption={selectedOptions[0]}
                actions={actions}
                multiple={false}
                state={actionsTreeState}
                materials={materials}
                meshes={meshes}
                cameras={cameras}
            />
        ) : (
            <ComposerPanel aria-label={T._('Option details', 'Admin')}>
                <ComposerEmptyState>{T._('Select an option to see its actions, settings and links.', 'Admin')}</ComposerEmptyState>
            </ComposerPanel>
        );

    const resizeInspector = (
        <ResizeBoundary label={T._('Resize properties panel', 'Admin')} onResize={setDetailsWidth} onReset={() => setDetailsWidth(undefined)} />
    );
    const detailsPanel = isAreaAttribute ? <ComposerPanel ref={setAreaDetailsHost} aria-label={T._('Area details', 'Admin')} /> : optionDetailsPanel;

    return (
        <WorkspaceGrid $propertiesPanel={propertiesPanel} $detailsWidth={detailsWidth} $hasOptionsPanel={hasDetailsPanel}>
            {propertiesPanel === 'side' ? (
                <>
                    {inspectorPanel}
                    {hasDetailsPanel && (
                        <>
                            <OptionsBoundary />
                            {detailsPanel}
                        </>
                    )}
                    {resizeInspector}
                    <ComposerPreview />
                </>
            ) : (
                <>
                    <ComposerPreview />
                    {resizeInspector}
                    {inspectorPanel}
                    {hasDetailsPanel && (
                        <>
                            <OptionsBoundary />
                            {detailsPanel}
                        </>
                    )}
                </>
            )}
        </WorkspaceGrid>
    );
};

export default ComposerAttributeContentPage;
